import { useTranslation } from 'react-i18next';
import type { AutofixRow } from '@ooh/contracts';
import { ExpandableTableRow } from '@ooh/ui/components/expandable-table-row';
import type { ExpandableTableCell } from '@ooh/ui/components/expandable-table-row';
import { HideEmptyToggle } from '@ooh/ui/components/hide-empty-toggle';
import { RowPreviewPanel } from '@ooh/ui/components/row-preview-panel';
import type { RowPreviewColumn } from '@ooh/ui/components/row-preview-panel';
import { formatCoordinate } from '@ooh/ui/lib/coordinate-format';
import { resolveValidationMsg, useExpandSet } from './validation-tables';
import type { TFn } from './validation-tables';

type AutofixPreviewRow = {
  row: string;
  nosnik: string;
  pole: string;
  wartoscOryginal: string;
  wartoscPoprawiona: string;
};

const AUTOFIX_COLUMNS: RowPreviewColumn<AutofixPreviewRow>[] = [
  { key: 'row', label: 'Wiersz' },
  { key: 'nosnik', label: 'Nośnik' },
  { key: 'pole', label: 'Pole' },
  { key: 'wartoscOryginal', label: 'Wartość oryginalna' },
  { key: 'wartoscPoprawiona', label: 'Wartość poprawiona' },
];

const AUTOFIX_HIGHLIGHT_FIELDS = ['Wartość poprawiona'];
/** Raw internal field keys for coordinate autofixes (see COORDINATE_FIELDS in validate-row.ts). */
const COORDINATE_AUTOFIX_FIELDS = new Set(['latitude', 'longitude']);

/**
 * Resolves the "Wartość poprawiona" display value. Latitude/longitude
 * corrections are re-formatted to a uniform, fixed decimal count (trailing
 * zeros preserved) — matching the media unit detail sidebar's coordinate
 * display — because the server-side autofix value keeps only the digits
 * needed to detect a real change, which strips trailing zeros
 * (CU-86cadb3fg regression).
 */
function formatFixedValueForDisplay(fix: AutofixRow): string {
  if (COORDINATE_AUTOFIX_FIELDS.has(fix.field) && fix.fixedValue !== undefined) {
    const parsed = Number(fix.fixedValue);
    if (Number.isFinite(parsed)) return formatCoordinate(parsed);
  }
  return fix.fixedValue ?? '';
}

function buildAutofixCells(
  fix: AutofixRow,
  emptyLabel: string,
  msg: string,
): ExpandableTableCell[] {
  return [
    { content: fix.row, className: 'px-4 py-2 font-mono text-muted-foreground' },
    {
      content: fix.grwId ?? '\u2014',
      className: 'px-4 py-2 font-mono text-xs text-foreground',
    },
    { content: fix.field, className: 'px-4 py-2 text-foreground' },
    {
      content:
        fix.originalValue !== undefined && fix.originalValue !== '' ? fix.originalValue : undefined,
      className: 'px-4 py-2 bg-red-50 text-red-700 font-mono',
      key: 'original',
    },
    {
      content: formatFixedValueForDisplay(fix),
      className: 'px-4 py-2 bg-green-50 text-green-700 font-mono font-semibold',
    },
    { content: msg, className: 'px-4 py-2 text-muted-foreground text-2xs' },
  ].map((cell) => ({
    ...cell,
    content:
      cell.key === 'original' && cell.content === undefined ? (
        <span className="italic font-sans text-red-400">{emptyLabel}</span>
      ) : (
        cell.content
      ),
  }));
}

interface AutofixTableProps {
  autofixes: AutofixRow[];
  hideEmpty: boolean;
  onHideEmptyChange: (next: boolean) => void;
  hideEmptyLabel: string;
}

export function AutofixTable({
  autofixes,
  hideEmpty,
  onHideEmptyChange,
  hideEmptyLabel,
}: AutofixTableProps) {
  const { t } = useTranslation('mediaImport');
  const { keys: expandedFixKeys, toggle, expandAll, collapseAll } = useExpandSet();

  const allFixKeys = autofixes.map((f) => `${f.row}-${f.field}`);
  const allExpanded = allFixKeys.length > 0 && allFixKeys.every((k) => expandedFixKeys.has(k));

  // colCount = 6 data cols + 1 toggle col
  const COL_COUNT = 7;

  return (
    <>
      <div className="flex items-center justify-end gap-4 px-4 py-1.5 border-b border-border/50">
        <HideEmptyToggle checked={hideEmpty} onChange={onHideEmptyChange} label={hideEmptyLabel} />
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={allExpanded ? collapseAll : () => expandAll(allFixKeys)}
          data-testid="autofix-expand-all"
        >
          {allExpanded ? t('validation.autofixCollapseAll') : t('validation.autofixExpandAll')}
        </button>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="text-left px-4 py-2 font-medium text-muted-foreground w-[70px]">
              {t('validation.tableHeaderRow')}
            </th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground w-[120px]">
              {t('validation.tableHeaderNosnik')}
            </th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground w-[130px]">
              {t('validation.tableHeaderField')}
            </th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground w-[160px]">
              {t('validation.tableHeaderOriginal')}
            </th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground w-[160px]">
              {t('validation.tableHeaderFixed')}
            </th>
            <th className="text-left px-4 py-2 font-medium text-muted-foreground">
              {t('validation.tableHeaderKind')}
            </th>
          </tr>
        </thead>
        <tbody>
          {autofixes.map((fix, fixIdx) => {
            const fixKey = `${fix.row}-${fix.field}`;
            const fixExpanded = expandedFixKeys.has(fixKey);
            // i18next's `t` expects statically-known key literals; the autofix `code` is server-driven.
            const fixMsg = resolveValidationMsg(
              t as unknown as TFn,
              fix.code,
              fix.message,
              fix.params,
            );
            const cells = buildAutofixCells(fix, t('validation.empty'), fixMsg);

            const previewRow: AutofixPreviewRow = {
              row: String(fix.row),
              nosnik: fix.grwId ?? '\u2014',
              pole: fix.field,
              wartoscOryginal: fix.originalValue ?? '',
              wartoscPoprawiona: formatFixedValueForDisplay(fix),
            };

            const visibleColumns = hideEmpty
              ? AUTOFIX_COLUMNS.filter(
                  (col) =>
                    (previewRow[col.key] !== '' && previewRow[col.key] !== '\u2014') ||
                    col.key === 'pole' ||
                    col.key === 'row',
                )
              : AUTOFIX_COLUMNS;

            return (
              <ExpandableTableRow
                key={fixKey}
                variant="success"
                expanded={fixExpanded}
                onToggle={() => toggle(fixKey)}
                toggleAriaLabel={
                  fixExpanded ? t('validation.collapseRow') : t('validation.expandRow')
                }
                toggleTestId={`autofix-expand-btn-${fix.row}`}
                colCount={COL_COUNT}
                isFirst={fixIdx === 0}
                // eslint-disable-next-line i18next/no-literal-string -- CSS utility class, not UI text
                hoverClassName="hover:bg-green-50/40"
                testId={`autofix-row-${fix.row}`}
                cells={cells}
              >
                <RowPreviewPanel<AutofixPreviewRow>
                  label={t('validation.rowDetailLabel', { row: fix.row })}
                  columns={visibleColumns}
                  row={previewRow}
                  highlightFields={AUTOFIX_HIGHLIGHT_FIELDS}
                  highlightColor="success"
                  colSpan={COL_COUNT}
                  emptyLabel={t('validation.empty')}
                  controls={
                    <HideEmptyToggle
                      checked={hideEmpty}
                      onChange={onHideEmptyChange}
                      label={hideEmptyLabel}
                    />
                  }
                  testId={`row-detail-panel-${fix.row}`}
                />
              </ExpandableTableRow>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
